// Copying a share link.
//
// A share link carries the whole comparison in its fragment, so it is long by
// construction — that is the privacy design, not a defect (§16.4). What we can
// control is whether a reader has to LOOK at it. Writing a `text/html` flavour
// alongside the plain one means Slack, Notion, Google Docs and most mail clients
// paste a titled hyperlink; the fragment is still there, just not on screen.
//
// The plain-text flavour stays the bare URL, deliberately: a terminal, a ticket
// field or a plain-text mail takes that flavour, and it has to be something you
// can click or paste into an address bar.
//
// The label is FIXED and generic on purpose. It is tempting to put the filename or
// the change count in it, and that would be the first time this app wrote user
// content into something destined for another application's document (G111).
export const SHARE_LINK_LABEL = 'View this comparison on cruxdiff';

// Attribute-safe escaping, local rather than imported from render/escape.js:
// src/lib does not depend on the render layer anywhere else, and this is a single
// href. lz-string's URI-safe alphabet contains `+ - $` but no `& " < >`; escaping
// anyway costs nothing and means a future change of encoder cannot make the anchor
// malformed.
const attr = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Copy `url` so that it pastes as a titled link where that is supported, and as
 * the bare URL everywhere else.
 * @returns 'rich' when both flavours were written, 'plain' when only the URL was.
 * @throws whatever the clipboard rejected with, when nothing could be written.
 */
export async function copyShareLink(url, { label = SHARE_LINK_LABEL, nav = globalThis.navigator, Item = globalThis.ClipboardItem } = {}) {
  const clipboard = nav?.clipboard;
  if (!clipboard) throw new Error('clipboard unavailable');

  if (typeof Item === 'function' && typeof clipboard.write === 'function' && typeof Blob === 'function') {
    try {
      const html = `<a href="${attr(url)}">${attr(label)}</a>`;
      await clipboard.write([
        new Item({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([url], { type: 'text/plain' }),
        }),
      ]);
      return 'rich';
    } catch {
      // Firefox before 127, older Safari, and any clipboard that refuses a
      // multi-flavour write. The URL still has to reach the clipboard.
    }
  }

  await clipboard.writeText(url);
  return 'plain';
}
